/* eslint-disable react/prop-types */
import React from 'react';
import { ThumbsUp, ThumbsDown, Hourglass, Table2 } from 'lucide-react';
import { Tooltip } from 'antd';
import {
  ACCEPT_CELL_COLOR,
  REJECT_CELL_COLOR,
  buildSyntheticTestItem,
  calculateInspectionTestStatus,
  extractValueParts,
  formatValueDisplayString,
  isTableJsonValue,
  isValueInRangeWithParams,
  resolveTestCriteriaType,
} from '../utils/inspectionLogCell';

const STATUS_COLUMNS = new Set(['overall_status', 'repair_status']);
const WELD_COLUMN = 'weld_id';

function renderStatusIcon(text) {
  if (text === 'accept') {
    return (
      <ThumbsUp
        className="log-status-icon log-status-icon--accept"
        size={20}
        strokeWidth={2}
        aria-label="Accept"
        style={{ color: ACCEPT_CELL_COLOR }}
      />
    );
  }
  if (text === 'reject') {
    return (
      <ThumbsDown
        className="log-status-icon log-status-icon--reject"
        size={20}
        strokeWidth={2}
        aria-label="Reject"
        style={{ color: REJECT_CELL_COLOR }}
      />
    );
  }
  if (text === 'pending') {
    return (
      <Tooltip title="Pending">
        <Hourglass
          className="log-status-icon log-status-icon--pending"
          size={20}
          strokeWidth={2}
          aria-hidden
        />
      </Tooltip>
    );
  }
  return text;
}

function renderColoredValueParts(parts, unit, comparator, minValue, maxValue) {
  const minV = minValue != null && minValue !== '' ? parseFloat(minValue) : NaN;
  const maxV = maxValue != null && maxValue !== '' ? parseFloat(maxValue) : NaN;
  const unitSuffix = unit ? ` ${unit}` : '';

  return parts.map((part, index) => {
    const num = parseFloat(part);
    const inRange =
      !Number.isNaN(num) && isValueInRangeWithParams(num, comparator, minV, maxV);
    const color = inRange ? undefined : REJECT_CELL_COLOR;

    return (
      <React.Fragment key={`${part}-${index}`}>
        {index > 0 ? ', ' : null}
        <span style={color ? { color } : undefined}>
          {part}
          {unitSuffix}
        </span>
      </React.Fragment>
    );
  });
}

const InspectionLogTableCell = ({
  text,
  column,
  testDef,
  testTypesList,
  onOpenTable,
}) => {
  const dataIndex = column?.dataIndex || column?.key;

  if (dataIndex === WELD_COLUMN) {
    return (
      <Tooltip placement="topLeft" title={text}>
        <span className="log-weld-cell-text">{text}</span>
      </Tooltip>
    );
  }

  if (STATUS_COLUMNS.has(dataIndex)) {
    return <div>{renderStatusIcon(text)}</div>;
  }

  if (text == null || String(text).trim() === '') {
    return <span>-</span>;
  }

  if (!testDef) {
    if (text === 'accept' || text === 'reject' || text === 'pending') {
      return <div>{renderStatusIcon(text)}</div>;
    }
    const raw = String(text).trim();
    if (raw.startsWith('[') && !isTableJsonValue(raw)) {
      return <span>{formatValueDisplayString(text, '')}</span>;
    }
    return (
      <Tooltip placement="topLeft" title={text}>
        <span>{text}</span>
      </Tooltip>
    );
  }

  const syntheticItem = buildSyntheticTestItem(text, testDef, testTypesList);
  const testType = resolveTestCriteriaType(testDef);
  const comparator = testDef.comparator;
  const hasComparator =
    comparator != null && String(comparator).trim() !== '' && comparator !== 'None';
  const unit = syntheticItem.test_value_type?.display_str;

  if (testType === 'Table' || isTableJsonValue(text)) {
    const status = calculateInspectionTestStatus(syntheticItem);
    const isReject = status === 'reject';
    return (
      <span style={isReject ? { color: REJECT_CELL_COLOR } : undefined}>
        <Table2
          role="button"
          tabIndex={0}
          className="log-table-open-icon"
          size={20}
          strokeWidth={2}
          aria-label="Open table results"
          style={{ color: 'inherit' }}
          onClick={() => onOpenTable?.(syntheticItem)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenTable?.(syntheticItem);
            }
          }}
        />
      </span>
    );
  }

  if (
    testType === 'Accept/Reject/NA' ||
    syntheticItem.test_value_type?.value_type === 'ACCEPT_OR_REJECT_OR_NA'
  ) {
    if (text === 'accept' || text === 'reject' || text === 'pending' || text === 'N/A') {
      return <div>{text === 'N/A' ? 'N/A' : renderStatusIcon(text)}</div>;
    }
  }

  if (testType === 'Value') {
    if (hasComparator) {
      const parts = extractValueParts(text);
      if (parts.length > 0) {
        return (
          <span>{renderColoredValueParts(parts, unit, comparator, testDef.minValue, testDef.maxValue)}</span>
        );
      }
    }

    const status = calculateInspectionTestStatus(syntheticItem);
    const color = status === 'reject' ? REJECT_CELL_COLOR : undefined;
    const display = formatValueDisplayString(text, unit);
    return <span style={color ? { color } : undefined}>{display}</span>;
  }

  if (extractValueParts(text).length > 0 && String(text).trim().startsWith('[') && !isTableJsonValue(String(text))) {
    const display = formatValueDisplayString(text, unit);
    return <span>{display}</span>;
  }

  if (text === 'accept' || text === 'reject' || text === 'pending') {
    return <div>{renderStatusIcon(text)}</div>;
  }

  return (
    <Tooltip placement="topLeft" title={text}>
      <span>{text}</span>
    </Tooltip>
  );
};

export default InspectionLogTableCell;
